/**
 * The one place browser-side PostHog events are captured (#2413).
 *
 * WHY THIS EXISTS. `posthog-js` is initialised lazily — the SDK import is
 * deferred to `requestIdleCallback` so analytics never competes with LCP (§14,
 * see `app/components/posthog-pageview.tsx`). That deferral means there is a
 * real window, on every cold load, in which the app wants to record an event
 * and the SDK does not exist yet. `posthog.capture()` on the uninitialised
 * module singleton does NOT buffer: it checks `__loaded && persistence &&
 * sessionPersistence && _requestQueue` and otherwise **discards the event**
 * with a console warning. So every caller that fired on mount was throwing its
 * event away on a cold load.
 *
 * Three defects came out of each caller improvising around that (#2413):
 *
 *  1. `PostHogPageview` kept its own one-slot queue plus a separate
 *     `posthogReady` state, and the direct-capture branch never cleared the
 *     slot. Because the SDK handle was set when `init()` RETURNED while
 *     `posthogReady` was set from the `loaded` callback — one remote-config
 *     round trip later — a navigation inside that window captured the new URL
 *     and then replayed the stale queued one afterwards. `/a` → `/b` → `/a`
 *     recorded `/a` twice, out of order, one of them timestamped while the
 *     user was on `/b`.
 *  2. That queue held a single URL and overwrote it, so a navigation before
 *     the idle callback fired silently dropped the landing pageview.
 *  3. `TrackEvent` and `NotFoundTracker` called the module singleton directly,
 *     so `camp_viewed` and `page_not_found` were discarded outright on a cold
 *     load — the events that measure exactly the pages #2401 made crawlable.
 *
 * THE SHAPE: callers use `capture()`, which either sends immediately or queues
 * in order; the SDK is handed over exactly once via `registerPostHog()`, which
 * drains the queue. There is a single readiness signal, so the skew in (1)
 * cannot reappear.
 *
 * UNIVERSAL AS OF #2418. Every browser-side capture in the tree goes through
 * here — the 18 components that used to import `posthog-js` and call
 * `posthog.capture()` directly have been migrated, and
 * `tests/contracts/posthog-direct-capture-allowlist-contract.test.ts` now pins
 * the set of direct callers as EMPTY. FOUR of them captured inside a mount
 * effect — `paywall_shown`, `password_nudge_shown`, and `premium_preview_viewed`
 * in two places — and were losing those events on every cold load. All four are
 * impression events, which skewed every conversion rate measured against them:
 * the impression undercounted while the click it was compared to, firing from a
 * user handler long after the idle callback, did not.
 *
 * `app/components/posthog-pageview.tsx` is the one module that holds the SDK.
 * It performs the deferred import and hands the instance to `registerPostHog`.
 * Nothing else should import `posthog-js` at all.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * The only thing this module needs from the SDK. Structural rather than
 * `PostHog` from posthog-js, because the object handed to the `loaded`
 * callback is typed `PostHogInterface`, and pinning either concrete type
 * couples this helper to posthog-js's type churn for no benefit.
 */
type PostHogCapture = {
  capture: (event: string, properties?: Record<string, unknown>) => unknown;
};

/** Set once the SDK is fully ready (from posthog's own `loaded` callback). */
let client: PostHogCapture | null = null;

type QueuedEvent = {
  event: string;
  properties?: Record<string, unknown>;
};

/**
 * Events captured before the SDK was ready, oldest first. Bounded so a page
 * that never initialises PostHog — a missing key, a blocked CDN, a user who
 * leaves before the browser goes idle — cannot grow it without limit. Dropping
 * the OLDEST keeps the most recent activity, which is what a session replay of
 * the queue is worth.
 */
const queue: QueuedEvent[] = [];
const MAX_QUEUED_EVENTS = 50;

/** Analytics are off entirely without a key — local dev and PR previews. */
export function isPostHogEnabled(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
}

/**
 * Hand the initialised SDK over and flush everything captured while waiting.
 *
 * Called exactly once, from posthog's `loaded` callback — not when `init()`
 * returns. Those are two different moments (a remote-config round trip apart),
 * and treating them as one is what produced the out-of-order replay in (1).
 * The queue is drained in one splice so a `capture()` racing this flush cannot
 * be sent twice.
 */
export function registerPostHog(ph: PostHogCapture): void {
  client = ph;
  const pending = queue.splice(0, queue.length);
  for (const item of pending) {
    try {
      ph.capture(item.event, item.properties);
    } catch (err) {
      // Analytics must never break the page.
      console.error("[posthog-browser] queued capture failed", item.event, err);
    }
  }
}

/**
 * Capture an event, or queue it in order until the SDK is ready.
 *
 * Safe to call from a mount effect on a cold load — that is the whole point.
 */
export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!isPostHogEnabled()) return;
  if (client) {
    try {
      client.capture(event, properties);
    } catch (err) {
      console.error("[posthog-browser] capture failed", event, err);
    }
    return;
  }
  if (queue.length >= MAX_QUEUED_EVENTS) queue.shift();
  queue.push({ event, properties });
}

/**
 * The plan a signed-in parent is identified with: "premium" while they hold an
 * active subscription, else "free". A subscription belongs to its parent_id:
 * premium_subscriptions has no user_id column (production, 2026-09-29; no
 * migration defines one). Filtering on it failed this read with 42703, the
 * error was dropped, and every premium parent was identified as "free". A
 * failed read still answers "free" (analytics must never break the page), but
 * is logged.
 */
export async function resolveAnalyticsPlanType(
  client: Pick<SupabaseClient, "from">,
  userId: string,
): Promise<"premium" | "free"> {
  try {
    const { data, error } = await client
      .from("premium_subscriptions")
      .select("status")
      .eq("parent_id", userId)
      .eq("status", "active")
      .maybeSingle();
    if (error) {
      console.error("[posthog-browser] plan lookup failed", error);
      return "free";
    }
    return data ? "premium" : "free";
  } catch (err) {
    console.error("[posthog-browser] plan lookup failed", err);
    return "free";
  }
}

/** Test seam: reset module state between cases. Not used by app code. */
export function __resetPostHogBrowserForTests(): void {
  client = null;
  queue.length = 0;
}

/** Test seam: inspect what is waiting. Not used by app code. */
export function __queuedEventsForTests(): ReadonlyArray<QueuedEvent> {
  return queue;
}
